import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Save, Loader2, Upload, Image as ImageIcon, Layers } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { getCompanyProfile, updateCompanyProfile, getInvoiceTiers, updateTierPricing, getFeatureFlags, updateFeatureFlags } from '../../config/api';
import { StateSelect, CountrySelect, CityInput } from '../../components/geo/GeoSelects';

const TIER_OPTS = [
  { value: '', label: 'Not set' },
  { value: 'TIER_1', label: 'Tier 1 — Under $10M ($45,000/yr)' },
  { value: 'TIER_2', label: 'Tier 2 — $10M–$100M ($105,000/yr)' },
  { value: 'TIER_3', label: 'Tier 3 — $100M–$500M+ ($200,000/yr)' }
];

const Field = ({ label, name, value, onChange, placeholder, type = 'text', disabled }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input type={type} value={value || ''} disabled={disabled}
      onChange={(e) => onChange(name, e.target.value)} placeholder={placeholder}
      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-500" />
  </div>
);

export default function CompanyProfile() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole ? hasRole(['ADMIN']) : false;
  const [p, setP] = useState({});
  const [tiers, setTiers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingTiers, setSavingTiers] = useState(false);
  const [flags, setFlags] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [prof, tr, ff] = await Promise.all([getCompanyProfile(), getInvoiceTiers(), getFeatureFlags().catch(() => ({}))]);
      setP(prof); setTiers(tr || {}); setFlags(ff || {});
    } catch (e) { toast.error('Failed to load company profile'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleFlag = async (key) => {
    if (!isAdmin) return;
    const next = { ...flags, [key]: !flags[key] };
    setFlags(next); // optimistic
    try { setFlags(await updateFeatureFlags({ [key]: next[key] })); toast.success('Modules updated'); }
    catch (e) { setFlags(flags); toast.error('Failed to update modules'); }
  };

  const setTierField = (key, field, value) => setTiers((t) => ({ ...t, [key]: { ...t[key], [field]: value } }));
  const saveTiers = async () => {
    setSavingTiers(true);
    try {
      const payload = {};
      for (const k of ['TIER_1', 'TIER_2', 'TIER_3']) if (tiers[k]) payload[k] = { base: Number(tiers[k].base), includedClaims: Number(tiers[k].includedClaims), overageRate: Number(tiers[k].overageRate) };
      const saved = await updateTierPricing(payload);
      setTiers(saved);
      toast.success('Tier pricing saved');
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to save pricing'); }
    finally { setSavingTiers(false); }
  };

  const set = (name, value) => setP((prev) => ({ ...prev, [name]: value }));

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 400 * 1024) return toast.error('Logo must be under 400 KB');
    const reader = new FileReader();
    reader.onload = () => set('logo', reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await updateCompanyProfile({
        name: p.name, legalName: p.legalName, taxId: p.taxId, ein: p.ein, dunsNumber: p.dunsNumber,
        addressLine1: p.addressLine1, addressLine2: p.addressLine2, city: p.city, state: p.state, zipCode: p.zipCode, country: p.country,
        phone: p.phone, email: p.email, website: p.website, logo: p.logo,
        billingTier: p.billingTier, commercialNdcCount: p.commercialNdcCount, erpIntegration: p.erpIntegration, billingEmail: p.billingEmail
      });
      setP(saved);
      toast.success('Company profile saved');
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-16 text-center text-gray-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900"><Building2 className="h-5 w-5 text-primary-600" /> Company Profile</h2>
          <p className="text-sm text-gray-500">The pharmaceutical company this platform serves. Shown across the app, on invoices, and reports.</p>
        </div>
        {isAdmin && <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>}
      </div>

      {!isAdmin && <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">Only the platform owner (Admin) can edit the company profile.</p>}

      {/* Identity + logo */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h3 className="mb-4 text-sm font-semibold text-gray-900">Identity</h3>
        <div className="flex flex-col gap-6 md:flex-row">
          <div className="md:w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
            <div className="flex h-28 w-full items-center justify-center overflow-hidden rounded-lg border border-dashed border-gray-300 bg-gray-50">
              {p.logo ? <img src={p.logo} alt="logo" className="max-h-full max-w-full object-contain" /> : <ImageIcon className="h-8 w-8 text-gray-300" />}
            </div>
            {isAdmin && (
              <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-800">
                <Upload className="h-3.5 w-3.5" /> Upload logo
                <input type="file" accept="image/*" onChange={onLogo} className="hidden" />
              </label>
            )}
          </div>
          <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Company name" name="name" value={p.name} onChange={set} placeholder="e.g. Acme Pharmaceuticals, Inc." disabled={!isAdmin} />
            <Field label="Legal name" name="legalName" value={p.legalName} onChange={set} disabled={!isAdmin} />
            <Field label="Tax ID" name="taxId" value={p.taxId} onChange={set} disabled={!isAdmin} />
            <Field label="EIN" name="ein" value={p.ein} onChange={set} disabled={!isAdmin} />
            <Field label="DUNS" name="dunsNumber" value={p.dunsNumber} onChange={set} disabled={!isAdmin} />
            <Field label="Website" name="website" value={p.website} onChange={set} placeholder="https://" disabled={!isAdmin} />
          </div>
        </div>
      </div>

      {/* Contact + address */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h3 className="mb-4 text-sm font-semibold text-gray-900">Address & Contact</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Address line 1" name="addressLine1" value={p.addressLine1} onChange={set} disabled={!isAdmin} />
          <Field label="Address line 2" name="addressLine2" value={p.addressLine2} onChange={set} disabled={!isAdmin} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <CityInput value={p.city} state={p.state} disabled={!isAdmin}
              onChange={(e) => set('city', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
            <StateSelect value={p.state} disabled={!isAdmin}
              onChange={(e) => set('state', e.target.value)} />
          </div>
          <Field label="ZIP" name="zipCode" value={p.zipCode} onChange={set} disabled={!isAdmin} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
            <CountrySelect value={p.country} disabled={!isAdmin}
              onChange={(e) => set('country', e.target.value)} />
          </div>
          <Field label="Phone" name="phone" value={p.phone} onChange={set} disabled={!isAdmin} />
          <Field label="Email" name="email" value={p.email} onChange={set} type="email" disabled={!isAdmin} />
        </div>
      </div>

      {/* Platform billing */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h3 className="mb-1 text-sm font-semibold text-gray-900">Platform Billing</h3>
        <p className="mb-4 text-xs text-gray-500">Set by the platform owner — drives the auto-generated platform invoices.</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subscription tier</label>
            <select value={p.billingTier || ''} disabled={!isAdmin} onChange={(e) => set('billingTier', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500">
              {TIER_OPTS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <Field label="# Commercial NDCs" name="commercialNdcCount" value={p.commercialNdcCount} onChange={set} type="number" disabled={!isAdmin} />
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={!!p.erpIntegration} disabled={!isAdmin} onChange={(e) => set('erpIntegration', e.target.checked)} className="h-4 w-4" />
            ERP integration (+$15,000 setup)
          </label>
          <Field label="Billing email" name="billingEmail" value={p.billingEmail} onChange={set} type="email" disabled={!isAdmin} />
        </div>
      </div>

      {/* Tier pricing (platform owner's price book) */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900"><Layers className="h-4 w-4 text-primary-600" /> Tier Pricing</h3>
          {isAdmin && <button onClick={saveTiers} disabled={savingTiers} className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700 disabled:opacity-50">{savingTiers ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save pricing</button>}
        </div>
        <p className="mb-3 text-xs text-gray-500">Base annual fee + included claims/year + per-claim overage for each tier. Drives invoice generation.</p>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-400">
                <th className="px-2 py-2 font-medium">Tier</th>
                <th className="px-2 py-2 font-medium">Base $/yr</th>
                <th className="px-2 py-2 font-medium">Included claims/yr</th>
                <th className="px-2 py-2 font-medium">Overage $/claim</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {['TIER_1', 'TIER_2', 'TIER_3'].map((k) => (
                <tr key={k}>
                  <td className="px-2 py-2 text-xs text-gray-600">{tiers[k]?.label || k}</td>
                  <td className="px-2 py-1"><input type="number" min="0" step="1000" disabled={!isAdmin} value={tiers[k]?.base ?? ''} onChange={(e) => setTierField(k, 'base', e.target.value)} className="w-32 rounded-md border border-gray-300 px-2 py-1.5 text-sm disabled:bg-gray-50" /></td>
                  <td className="px-2 py-1"><input type="number" min="0" step="1000" disabled={!isAdmin} value={tiers[k]?.includedClaims ?? ''} onChange={(e) => setTierField(k, 'includedClaims', e.target.value)} className="w-32 rounded-md border border-gray-300 px-2 py-1.5 text-sm disabled:bg-gray-50" /></td>
                  <td className="px-2 py-1"><input type="number" min="0" step="0.25" disabled={!isAdmin} value={tiers[k]?.overageRate ?? ''} onChange={(e) => setTierField(k, 'overageRate', e.target.value)} className="w-28 rounded-md border border-gray-300 px-2 py-1.5 text-sm disabled:bg-gray-50" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Optional modules — per-company feature flags */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-1 flex items-center gap-2"><Layers className="h-5 w-5 text-primary-700" /><h2 className="text-base font-semibold text-gray-900">Optional Modules</h2></div>
        <p className="mb-4 text-sm text-gray-500">Turn on only the government programs this company is obligated to. Off modules are hidden from everyone.</p>
        {[
          { key: 'iraPartB', name: 'IRA Inflation Rebate — Medicare Part B', desc: 'Quarterly rebate on physician-administered drugs, off ASP.' },
          { key: 'iraPartD', name: 'IRA Inflation Rebate — Medicare Part D', desc: 'Annual rebate on covered Part D drugs, off AMP.' }
        ].map((m) => {
          const on = !!flags[m.key];
          return (
            <div key={m.key} className="flex items-center justify-between border-t border-gray-100 py-3 first:border-t-0">
              <div className="pr-4">
                <div className="font-medium text-gray-900">{m.name}</div>
                <div className="text-sm text-gray-500">{m.desc}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-medium ${on ? 'text-accent-600' : 'text-gray-400'}`}>{on ? 'On' : 'Off'}</span>
                <button
                  type="button" role="switch" aria-checked={on} disabled={!isAdmin}
                  onClick={() => toggleFlag(m.key)}
                  title={isAdmin ? (on ? 'Disable module' : 'Enable module') : 'Admin only'}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${on ? 'bg-accent-500' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${on ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
